import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { marksToText, unresolvedPhotos, unresolvedPlaceholders } from './mailSend';
import type { InlinePhoto } from './emailPhotos';
import { threadErrorMessage } from './mailThread';
import type { MailMessage } from './mailThread';

/**
 * The browser's side of sending as the sales mailbox.
 *
 * Two hooks. `useCanSendAsMailbox` decides whether to DRAW the Send button;
 * `useSendAsMailbox` presses it. Only the second one matters for security, and
 * even it only carries the request — the decision is the Edge Function's
 * (supabase/functions/send-as-mailbox). See docs/sales-inbox-design-note.md,
 * item 3: a hidden button with no server check is a decoration.
 */

export const SALES_MAILBOX = 'alexjschulze@4ocean.com';

/**
 * Whether the signed-in person is on the sender list.
 *
 * Reads `mailbox_senders`, whose RLS returns only YOUR OWN row — so the query
 * is "is there a row" and never "who else is on the list". One row means the
 * button is drawn. Zero means the person gets the draft flow, as today.
 */
export function useCanSendAsMailbox() {
  const { client } = useAuth();
  return useQuery({
    queryKey: ['mailbox-senders', 'me', SALES_MAILBOX] as const,
    queryFn: async () => {
      const { data, error } = await client
        .from('mailbox_senders')
        .select('user_id')
        .eq('mailbox_email', SALES_MAILBOX)
        .limit(1);
      if (error) throw error;
      return (data ?? []).length > 0;
    },
    // The list changes when Kyu adds a row, which is rare. Refetching it on
    // every drawer open would be wasted round trips.
    staleTime: 5 * 60 * 1000,
  });
}

export interface SendAsMailboxInput {
  leadId: string;
  subject: string;
  body: string;
  /** Shown on the lead's timeline: "Email 2 · CG New Lead Template". */
  note: string;
  /**
   * Gmail id of the message this answers, from the thread view. The function
   * looks it up and checks the lead is on it before threading under it.
   */
  replyTo?: string;
  /** Files to send with it. Kyu: attachments are "mostly in the next few emails". */
  attachments?: File[];
  /**
   * Photos that sit IN the body, already resolved (emailPhotos.ts): the body
   * refers to each as cid:<cid>. They count toward the same file caps.
   */
  inlinePhotos?: InlinePhoto[];
}

/** The most files, and the most bytes, one email may carry. Mirrors the function. */
export const MAX_ATTACHMENTS = 10;
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

export interface SendAsMailboxResult {
  sent: true;
  gmailId: string;
  threadId: string | null;
  /** True when this went out as a reply in the lead's existing conversation. */
  threaded: boolean;
  /** How many files went with it. */
  attachments: number;
  /** What log_mailbox_send decided: ticked, logged, already — or unrecorded. */
  outcome: 'ticked' | 'logged' | 'already' | 'unrecorded';
  recordError?: string;
}

/**
 * The refusals the function can return, translated for the person reading
 * them. Each names what to DO, because "send failed" in a sales tool is a lead
 * nobody follows up.
 */
export function sendErrorMessage(code: string, detail?: unknown): string {
  switch (code) {
    case 'unresolved_placeholder': {
      const list = Array.isArray(detail) ? detail.join(', ') : '';
      return `The copy still has a placeholder nobody filled in${list ? `: ${list}` : ''}. Fix the copy set and try again.`;
    }
    case 'unresolved_photo': {
      const list = Array.isArray(detail) ? detail.join(', ') : '';
      return `A photo named in the email was not attached${list ? ` (${list})` : ''}. Nothing was sent.`;
    }
    case 'not_a_sender':
      return 'Your account is not on the list of people who can send as Alex.';
    case 'not_signed_in':
      return 'Your session has expired. Sign out and back in.';
    case 'lead_has_no_email':
      return 'This lead has no email address.';
    case 'lead_on_hold':
      return holdErrorMessage(detail);
    case 'lead_not_found':
      return 'This lead no longer exists.';
    case 'reply_not_this_lead':
      return 'That conversation does not include this lead, so the reply was not sent.';
    case 'mailbox_not_connected':
    case 'reauthorise_needed':
      return 'The sales mailbox needs to be reconnected. Tell Kyu.';
    case 'signature_unavailable':
      return "Could not read Alex's signature from Gmail. Try again in a moment.";
    case 'send_not_permitted':
      return 'Gmail refused to send: the mailbox connection is missing the send permission. Tell Kyu.';
    case 'gmail_refused':
      return 'Gmail refused the message. Nothing was sent.';
    default:
      return 'The email could not be sent. Nothing went out.';
  }
}

function holdErrorMessage(detail: unknown): string {
  const hold = detail !== null && typeof detail === 'object' ? detail : {};
  const until = 'until' in hold && typeof hold.until === 'string' ? hold.until : null;
  const note = 'note' in hold && typeof hold.note === 'string' ? hold.note.trim() : '';
  const message =
    until === null
      ? 'This lead is on hold, so nothing was sent.'
      : `This lead is on hold until ${until}, so nothing was sent.`;
  return note === '' ? message : `${message} [${note}]`;
}

/**
 * Sends. The session token rides along in Authorization — supabase-js does
 * that for every functions.invoke while somebody is signed in — and that is
 * how the function knows who pressed the button.
 *
 * The placeholder check runs here too, before the request, so the person gets
 * the message instantly. The function runs the same check regardless: the
 * browser's copy is speed, the server's is the guarantee.
 */
export function useSendAsMailbox() {
  const { client } = useAuth();
  return useMutation({
    mutationFn: async (input: SendAsMailboxInput): Promise<SendAsMailboxResult> => {
      const placeholders = unresolvedPlaceholders(`${input.subject}\n${marksToText(input.body)}`);
      if (placeholders.length > 0) {
        throw new Error(sendErrorMessage('unresolved_placeholder', placeholders));
      }
      const photos = unresolvedPhotos(input.body);
      if (photos.length > 0) {
        throw new Error(sendErrorMessage('unresolved_photo', photos));
      }
      const files = input.attachments ?? [];
      const inline = input.inlinePhotos ?? [];
      if (files.length + inline.length > MAX_ATTACHMENTS) {
        throw new Error(`No more than ${MAX_ATTACHMENTS} files and photos on one email.`);
      }
      const totalBytes =
        files.reduce((n, f) => n + f.size, 0) + inline.reduce((n, p) => n + p.file.size, 0);
      if (totalBytes > MAX_ATTACHMENT_BYTES) {
        throw new Error(
          'The files and photos add up to more than 20 MB. Send fewer, or smaller, ones.',
        );
      }

      // JSON for a plain email. Files need multipart/form-data, which the
      // browser builds from a FormData body with its own boundary.
      let body: FormData | Record<string, string>;
      if (files.length === 0 && inline.length === 0) {
        body = {
          leadId: input.leadId,
          subject: input.subject,
          body: input.body,
          note: input.note,
          ...(input.replyTo ? { replyTo: input.replyTo } : {}),
        };
      } else {
        const form = new FormData();
        form.set('leadId', input.leadId);
        form.set('subject', input.subject);
        form.set('body', input.body);
        form.set('note', input.note);
        if (input.replyTo) form.set('replyTo', input.replyTo);
        for (const file of files) form.append('attachment', file, file.name);
        for (const photo of inline) form.append('inline', photo.file, photo.file.name);
        if (inline.length > 0) form.set('inlineCids', JSON.stringify(inline.map((p) => p.cid)));
        body = form;
      }

      const result = (await client.functions.invoke('send-as-mailbox', { body })) as {
        data: SendAsMailboxResult | null;
        error: { context?: Response; message?: string } | null;
      };

      if (result.error !== null) {
        // supabase-js hands back the raw Response on a non-2xx. The body is
        // where the function said WHY.
        let code = 'unknown';
        let detail: unknown;
        try {
          const body = (await result.error.context?.json()) as
            | {
                error?: string;
                placeholders?: unknown;
                photos?: unknown;
                detail?: unknown;
                until?: unknown;
                note?: unknown;
              }
            | undefined;
          code = body?.error ?? code;
          detail =
            body?.placeholders ??
            body?.photos ??
            body?.detail ??
            (body?.error === 'lead_on_hold' ? { until: body.until, note: body.note } : undefined);
        } catch {
          // No JSON body. The default message covers it.
        }
        throw new Error(sendErrorMessage(code, detail));
      }
      if (result.data === null || result.data.sent !== true) {
        throw new Error(sendErrorMessage('unknown'));
      }
      return result.data;
    },
  });
}

export interface LeadMailThreadResult {
  mailbox: string;
  lead: string;
  messages: MailMessage[];
  /** True when the lead has more conversations than the function returns. */
  truncated: boolean;
}

/**
 * The lead's conversation with the sales mailbox, read live.
 *
 * Fetched when the Emails view opens and not before (`enabled`), held for a
 * few minutes so switching tabs and back does not re-read Gmail, and gone when
 * the page is. No store, by design — docs/sales-inbox-design-note.md, item 5.
 *
 * The function decides who may read: it looks the lead up through the
 * caller's own session, so a person who cannot see the lead gets
 * `lead_not_found`, the same answer the leads page would give.
 */
export function useLeadMailThreads(leadId: string, enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: ['lead-mail-threads', leadId] as const,
    enabled,
    staleTime: 3 * 60 * 1000,
    retry: false,
    queryFn: async (): Promise<LeadMailThreadResult> => {
      const result = (await client.functions.invoke('lead-mail-thread', {
        body: { leadId },
      })) as {
        data: LeadMailThreadResult | null;
        error: { context?: Response; message?: string } | null;
      };
      if (result.error !== null) {
        let code = 'unknown';
        try {
          const body = (await result.error.context?.json()) as { error?: string } | undefined;
          code = body?.error ?? code;
        } catch {
          // No JSON body. The default message covers it.
        }
        throw new Error(threadErrorMessage(code));
      }
      if (result.data === null || !Array.isArray(result.data.messages)) {
        throw new Error(threadErrorMessage('unknown'));
      }
      return result.data;
    },
  });
}
